import React, { useCallback, useEffect } from 'react';
import { useHistory } from 'react-router-dom';

import {
  Table,
  TableRow,
  TableBody,
  TableCell,
  TableSortLabel,
  TableHead,
  TableContainer,
  LinearProgress,
  TablePagination
} from '@material-ui/core';

import TaskStatusLabel from '../../../common/TaskStatusLabel';
import WrappedTableCell from '../../../common/WrappedTableCell';
import { ProductBoardColumnEntity } from '../board/ProductBoardColumn';
import {
  useQueryParam,
  NumberParam,
  StringParam,
  withDefault
} from 'use-query-params';
import {
  useProductEntityPrefix,
  useFindProductDesignOutputs
} from '../Product.hooks';
import {
  BaseProductEntityTableProps,
  ProductComponent,
  ProductDesignOutput
} from '../Product.interface';
import { AppSnackbar } from '@sierralabs/react-material-ui';
import { getProductEntityName } from '../Product.config';
import PageTemplateCardOver from '../../../common/page-template/PageTemplateCardOver';
import { stripHtml } from '../../../common/formatter';

const DesignOutputsTable: React.FC<BaseProductEntityTableProps> = (
  props: BaseProductEntityTableProps
) => {
  const { productId, apiParams, fetchRef } = props;
  const history = useHistory();
  const [page, setPage] = useQueryParam('page', withDefault(NumberParam, 0));
  const [limit, setLimit] = useQueryParam(
    'limit',
    withDefault(NumberParam, 100)
  );
  const [order, setOrder] = useQueryParam('order', StringParam);
  const [direction, setDirection] = useQueryParam('direction', StringParam);

  const {
    productDesignOutputList,
    isLoadingProductDesignOutputs,
    loadingProductDesignOutputsError,
    findProductDesignOutputs
  } = useFindProductDesignOutputs(productId);

  const findProductDesignOutputsWithParams = useCallback(() => {
    findProductDesignOutputs({ page, limit, order, direction, ...apiParams });
  }, [findProductDesignOutputs, page, limit, order, direction, apiParams]);

  useEffect(() => {
    if (fetchRef) {
      fetchRef.current = findProductDesignOutputsWithParams;
    }
    findProductDesignOutputsWithParams();
  }, [fetchRef, findProductDesignOutputsWithParams]);

  const prefix: string = useProductEntityPrefix(
    ProductBoardColumnEntity.DesignOutput
  );

  const handleSort = (column: string) => {
    setOrder(column);
    setDirection(direction === 'ASC' ? 'DESC' : 'ASC');
  };

  const getSortDirection = (column: string): 'asc' | 'desc' => {
    return order === column && direction === 'DESC' ? 'desc' : 'asc';
  };

  return (
    <PageTemplateCardOver>
      <TableContainer>
        <Table stickyHeader>
          <TableHead>
            <TableRow>
              <TableCell>
                <TableSortLabel
                  active={order === 'productDesignOutput.id'}
                  direction={getSortDirection('productDesignOutput.id')}
                  onClick={() => handleSort('productDesignOutput.id')}
                >
                  ID
                </TableSortLabel>
              </TableCell>
              <TableCell>
                <TableSortLabel
                  active={order === 'productDesignOutput.title'}
                  direction={getSortDirection('productDesignOutput.title')}
                  onClick={() => handleSort('productDesignOutput.title')}
                >
                  Title
                </TableSortLabel>
              </TableCell>
              <TableCell>
                <TableSortLabel
                  active={order === 'productDesignOutput.description'}
                  direction={getSortDirection(
                    'productDesignOutput.description'
                  )}
                  onClick={() => handleSort('productDesignOutput.description')}
                >
                  Description
                </TableSortLabel>
              </TableCell>
              <TableCell>
                <TableSortLabel
                  active={order === 'productDesignOutput.createdBy'}
                  direction={getSortDirection('productDesignOutput.createdBy')}
                  onClick={() => handleSort('productDesignOutput.createdBy')}
                >
                  Author
                </TableSortLabel>
              </TableCell>
              <TableCell>
                <TableSortLabel
                  active={order === 'task.user'}
                  direction={getSortDirection('task.user')}
                  onClick={() => handleSort('task.user')}
                >
                  Assignee
                </TableSortLabel>
              </TableCell>
              <TableCell>
                <TableSortLabel
                  active={order === 'task.status'}
                  direction={getSortDirection('task.status')}
                  onClick={() => handleSort('task.status')}
                >
                  Status
                </TableSortLabel>
              </TableCell>
              <TableCell>
                <TableSortLabel
                  active={order === 'components.name'}
                  direction={getSortDirection('components.name')}
                  onClick={() => handleSort('components.name')}
                >
                  Component
                </TableSortLabel>
              </TableCell>
              <TableCell>
                <TableSortLabel
                  active={order === 'release.name'}
                  direction={getSortDirection('release.name')}
                  onClick={() => handleSort('release.name')}
                >
                  Release
                </TableSortLabel>
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {isLoadingProductDesignOutputs && (
              <TableRow>
                <TableCell colSpan={7} padding='none'>
                  <LinearProgress />
                </TableCell>
              </TableRow>
            )}
            {productDesignOutputList?.data?.map(
              (designOutput: ProductDesignOutput) => (
                <TableRow
                  hover
                  key={designOutput.id}
                  onClick={() =>
                    history.push(
                      `/portal/products/${productId}/design-outputs/${designOutput.id}`
                    )
                  }
                >
                  <TableCell>{prefix + designOutput.id}</TableCell>
                  <WrappedTableCell>{designOutput.title}</WrappedTableCell>
                  <WrappedTableCell>
                    <div className='truncate'>
                      {stripHtml(designOutput.description || '')}
                    </div>
                  </WrappedTableCell>
                  <TableCell>
                    {`${designOutput.task.createdBy?.firstName} ${designOutput.task.createdBy?.lastName}`}
                  </TableCell>
                  <TableCell>
                    {designOutput.task.user
                      ? `${designOutput.task.user?.firstName} ${designOutput.task.user.lastName}`
                      : 'No User Assigned'}
                  </TableCell>
                  <TableCell>
                    <TaskStatusLabel task={designOutput.task} />
                  </TableCell>
                  <TableCell>
                    {designOutput.components.length !== 0
                      ? designOutput.components
                          .map((component: ProductComponent) => component.name)
                          .join(', ')
                      : 'No Components'}
                  </TableCell>
                  <TableCell>
                    {designOutput.release
                      ? designOutput.release.name
                      : 'No Release'}
                  </TableCell>
                </TableRow>
              )
            )}
          </TableBody>
        </Table>
      </TableContainer>
      <TablePagination
        rowsPerPageOptions={[10, 25, 100]}
        component='div'
        count={productDesignOutputList?.total || 0}
        rowsPerPage={limit}
        page={page}
        onPageChange={(_, newPage) => setPage(newPage)}
        onRowsPerPageChange={event => {
          setLimit(+event.target.value);
          setPage(0);
        }}
      />
      <AppSnackbar
        severity='error'
        open={Boolean(loadingProductDesignOutputsError)}
      >
        There was an error loading the list of{' '}
        {getProductEntityName('designOutput')}.
      </AppSnackbar>
    </PageTemplateCardOver>
  );
};

export default DesignOutputsTable;
